import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import * as React from 'react'
import { cn } from 'src/lib/utils'

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>): React.ReactElement => (
  <nav role="navigation" aria-label="pagination" className={cn('mx-auto flex w-full justify-center', className)} {...props} />
)
Pagination.displayName = 'Pagination'

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(({ className, ...props }, ref) => (
  <ul ref={ref} className={cn('flex flex-row items-center gap-2', className)} {...props} />
))
PaginationContent.displayName = 'PaginationContent'

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
))
PaginationItem.displayName = 'PaginationItem'

// Every control is the same square, copper-outlined box (GH-775 Figma).
const PAGINATION_BOX =
  'flex h-10 w-10 items-center justify-center rounded-md border text-base font-evogria leading-none transition-colors'

type PaginationLinkProps = {
  isActive?: boolean
  disabled?: boolean
} & React.ComponentProps<'a'>

const PaginationLink = ({ className, isActive, disabled, onClick, ...props }: PaginationLinkProps): React.ReactElement => (
  <a
    aria-current={isActive ? 'page' : undefined}
    aria-disabled={disabled || undefined}
    tabIndex={disabled ? -1 : undefined}
    onClick={disabled ? undefined : onClick}
    className={cn(
      PAGINATION_BOX,
      'cursor-pointer border-primary text-primary hover:bg-primary/10',
      // Active page reads as a filled copper chip.
      isActive && 'bg-primary text-white hover:bg-primary',
      // Disabled arrow: muted grey outline, dimmed, no click/hover.
      disabled && 'pointer-events-none cursor-default border-bright-grey text-bright-grey opacity-60 hover:bg-transparent',
      className,
    )}
    {...props}
  />
)
PaginationLink.displayName = 'PaginationLink'

const PaginationPrevious = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>): React.ReactElement => (
  <PaginationLink aria-label="Go to previous page" {...props}>
    <ChevronLeft className="h-5 w-5" />
  </PaginationLink>
)
PaginationPrevious.displayName = 'PaginationPrevious'

const PaginationNext = ({ className, ...props }: React.ComponentProps<typeof PaginationLink>): React.ReactElement => (
  <PaginationLink aria-label="Go to next page" {...props}>
    <ChevronRight className="h-5 w-5" />
  </PaginationLink>
)
PaginationNext.displayName = 'PaginationNext'

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>): React.ReactElement => (
  <span aria-hidden className={cn('flex h-10 w-10 items-center justify-center text-primary', className)} {...props}>
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = 'PaginationEllipsis'

export { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious }
